import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Eye, Hammer, MapPin } from "lucide-react";
import { aboutPillars, stats, locations } from "../data/mock";
import { openContactModal } from "../lib/contactBus";

const pillarIcons = [ShieldCheck, Eye, Hammer, MapPin];

export default function About() {
  return (
    <>
      {/* Hero */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 pt-16 pb-20">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8">
            <p className="section-tag mb-4">// ABOUT EDGE DEVELOPER, LLC</p>
            <h1 className="font-display text-[72px] sm:text-[112px] lg:text-[160px] uppercase leading-[0.88]">
              Twelve years.<br /><span className="text-[var(--terracotta)]">One promise.</span>
            </h1>
            <p className="mt-8 text-[15px] lg:text-[16px] leading-8 text-[var(--slate-700)] max-w-2xl">
              Edge Developer, LLC was founded in 2014 with a simple idea — do renovation differently. No subbing your project out to strangers. No vague timelines. No "unforeseen" budget surprises. Twelve years and 1,000+ homes later, we still build the same way: one accountable team, transparent budgets, and finishes engineered to outlast the trends that inspired them.
            </p>
            <div className="mt-10 flex gap-3">
              <button onClick={openContactModal} className="btn-terracotta">Start Project <ArrowRight size={15} /></button>
              <Link to="/projects" className="btn-outline">See The Work</Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 corner-frame relative min-h-[420px] overflow-hidden bg-[var(--slate-900)]">
            <img src="https://images.unsplash.com/photo-1681949103006-70066fb25dfe?w=1200&q=80" alt="Edge team" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--slate-950)]/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-[var(--off-white)]">
              <p className="font-mono-sans text-[10px] text-[var(--terracotta-light)]">// THE TEAM</p>
              <p className="font-display text-[28px] uppercase leading-tight mt-1">42 craftspeople</p>
              <p className="text-[12px] text-[var(--slate-300)] mt-1">All in-house. All on payroll.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 py-16">
        <p className="section-tag mb-3">// THE PILLARS</p>
        <h2 className="font-display text-[48px] sm:text-[64px] uppercase leading-[0.95] mb-10">How we operate.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
          {aboutPillars.map((p, i) => {
            const Icon = pillarIcons[i] || ShieldCheck;
            return (
              <div key={p.num} className="bento-card p-8 lg:p-10 flex gap-6 hover-lift">
                <div className="shrink-0">
                  <div className="w-14 h-14 bg-[var(--terracotta)] flex items-center justify-center text-[var(--off-white)]">
                    <Icon size={22} strokeWidth={1.8} />
                  </div>
                  <p className="font-display text-[34px] text-[var(--slate-500)] mt-4">{p.num}</p>
                </div>
                <div>
                  <h3 className="font-display text-[28px] uppercase leading-tight">{p.title}</h3>
                  <p className="text-[13.5px] leading-7 text-[var(--slate-700)] mt-3">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats wall */}
      <section className="bg-[var(--slate-950)] text-[var(--off-white)] py-20">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <p className="section-tag !text-[var(--terracotta-light)] mb-3">// BY THE NUMBERS</p>
          <h2 className="font-display text-[48px] sm:text-[64px] uppercase leading-[0.95] mb-10">A decade of <span className="text-[var(--terracotta)]">receipts.</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-[var(--slate-700)]">
            {stats.map((s) => (
              <div key={s.label} className="border-b border-r border-[var(--slate-700)] p-6 lg:p-8 hover:bg-[var(--slate-900)] transition-colors">
                <p className="font-display text-[44px] lg:text-[56px] text-[var(--terracotta)] leading-none">{s.value}</p>
                <p className="font-mono-sans text-[10.5px] text-[var(--slate-300)] mt-3">{s.label.toUpperCase()}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="max-w-[1480px] mx-auto px-6 lg:px-10 py-20">
        <p className="section-tag mb-3">// COVERAGE</p>
        <h2 className="font-display text-[48px] sm:text-[64px] uppercase leading-[0.95] mb-10">Where we work.</h2>
        <div className="grid grid-cols-12 gap-3 lg:gap-4">
          {locations.map((l) => (
            <Link key={l.slug} to="/locations" className="bento-card p-8 hover-lift block col-span-12 lg:col-span-7">
              <p className="font-mono-sans text-[10px] text-[var(--terracotta)] mb-2">// {l.region} — HEADQUARTERS</p>
              <h3 className="font-display text-[44px] sm:text-[56px] uppercase leading-[0.95]">{l.name}</h3>
              <p className="text-[13.5px] text-[var(--slate-700)] mt-3 leading-7">{l.coverage}</p>
              <p className="font-mono-sans text-[11px] text-[var(--slate-500)] mt-4">{l.address} · {l.phone}</p>
            </Link>
          ))}
          <div className="bento-card terracotta p-8 col-span-12 lg:col-span-5 flex flex-col justify-between min-h-[260px]">
            <div>
              <p className="font-mono-sans text-[10px] opacity-80 mb-2">// LOCAL ROOTS</p>
              <h3 className="font-display text-[32px] uppercase leading-tight">Built for southern Minnesota.</h3>
            </div>
            <p className="text-[13.5px] mt-4 opacity-95 leading-7">
              We live here, our kids go to school here, and most of our crew has been with us 5+ years. That means we treat every project like it's our neighbor's — because it usually is.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
